import React, { useId } from 'react';
import type { OnboardingIntake } from '../../types/onboardingIntake';
import {
  Button,
  Callout,
  Checkbox,
  Combobox,
  Dialog,
  Field,
  Input,
  Segmented,
  Select,
  Spinner,
} from '../../ui/v2';
import {
  intakeOptionLabel,
  parseSelectId,
  STORE_GROUP_USER_ROLES,
  STORE_USER_ROLES,
  type SelectOption,
  type StoreGroupUserFormData,
  type StoreGroupUserMode,
  type UserAssignmentType,
  type UserFormData,
} from './usersModel';

type ControlChange = React.ChangeEvent<HTMLInputElement | HTMLSelectElement>;

/**
 * Searchable picker matching the legacy SearchableSelect: optional pickers
 * offer the placeholder as a way back to "nothing selected", and required
 * ones keep the browser's required check through a hidden input.
 */
const Picker: React.FC<{
  id: string;
  describedBy?: string;
  options: SelectOption[];
  value: string | number;
  onChange: (value: string) => void;
  placeholder: string;
  required?: boolean;
  disabled?: boolean;
}> = ({
  id,
  describedBy,
  options,
  value,
  onChange,
  placeholder,
  required,
  disabled,
}) => {
  const current = value ? String(value) : '';
  const items = options.map((option) => ({
    value: String(option.value),
    label: option.label,
  }));
  return (
    <div className="relative">
      <Combobox
        id={id}
        aria-describedby={describedBy}
        options={
          !required && current
            ? [{ value: '', label: placeholder }, ...items]
            : items
        }
        value={current}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
      />
      {required && (
        <input
          tabIndex={-1}
          aria-hidden
          autoComplete="off"
          required
          value={current}
          onChange={() => {}}
          className="pointer-events-none absolute bottom-0 left-0 h-0 w-full opacity-0"
        />
      )}
    </div>
  );
};

const Messages: React.FC<{ error: string; success: string }> = ({
  error,
  success,
}) => (
  <>
    {error && <Callout tone="danger">{error}</Callout>}
    {success && (
      <p role="status" className="text-v2-12 text-v2-success">
        {success}
      </p>
    )}
  </>
);

const FormFooter: React.FC<{
  formId: string;
  submitLabel: string;
  onCancel: () => void;
}> = ({ formId, submitLabel, onCancel }) => (
  <>
    <Button variant="ghost" onClick={onCancel}>
      Cancel
    </Button>
    <Button variant="primary" type="submit" form={formId}>
      {submitLabel}
    </Button>
  </>
);

const ChoiceField = <K extends string>({
  label,
  items,
  value,
  onChange,
}: {
  label: string;
  items: { key: K; label: string }[];
  value: K;
  onChange: (key: K) => void;
}) => (
  <div className="flex flex-col gap-1.5">
    <span className="text-v2-12 font-medium text-v2-fg-2">{label}</span>
    <Segmented
      aria-label={label}
      items={items}
      value={value}
      onChange={onChange}
      className="self-start"
    />
  </div>
);

const ROLE_OPTIONS = STORE_USER_ROLES.map((role) => (
  <option key={role} value={role}>
    {role}
  </option>
));

const GROUP_ROLE_OPTIONS = STORE_GROUP_USER_ROLES.map((role) => (
  <option key={role.value} value={role.value}>
    {role.label}
  </option>
));

export const CreateUserDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  error: string;
  success: string;
  assignmentType: UserAssignmentType;
  onAssignmentTypeChange: (type: UserAssignmentType) => void;
  userForm: UserFormData;
  onUserChange: (e: ControlChange) => void;
  onPopulateFromIntake: () => void;
  storeOptions: SelectOption[];
  loadingStores: boolean;
  groupMode: StoreGroupUserMode;
  onGroupModeChange: (mode: StoreGroupUserMode) => void;
  groupForm: StoreGroupUserFormData;
  onGroupFormChange: React.Dispatch<
    React.SetStateAction<StoreGroupUserFormData>
  >;
  storeUserOptions: SelectOption[];
  loadingStoreUsers: boolean;
  storeGroupOptions: SelectOption[];
  loadingStoreGroups: boolean;
}> = ({
  open,
  onClose,
  onSubmit,
  error,
  success,
  assignmentType,
  onAssignmentTypeChange,
  userForm,
  onUserChange,
  onPopulateFromIntake,
  storeOptions,
  loadingStores,
  groupMode,
  onGroupModeChange,
  groupForm,
  onGroupFormChange,
  storeUserOptions,
  loadingStoreUsers,
  storeGroupOptions,
  loadingStoreGroups,
}) => {
  const formId = useId();
  const setGroupField =
    <F extends keyof StoreGroupUserFormData>(field: F) =>
    (value: StoreGroupUserFormData[F]) =>
      onGroupFormChange((prev) => ({ ...prev, [field]: value }));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Create User"
      size="lg"
      footer={
        <FormFooter
          formId={formId}
          submitLabel={
            assignmentType === 'store'
              ? 'Create User'
              : 'Create Store Group User'
          }
          onCancel={onClose}
        />
      }
    >
      <form id={formId} onSubmit={onSubmit} className="flex flex-col gap-4">
        <ChoiceField
          label="User Type"
          items={[
            { key: 'store', label: 'Store user' },
            { key: 'store_group', label: 'Store group user' },
          ]}
          value={assignmentType}
          onChange={onAssignmentTypeChange}
        />
        {assignmentType === 'store' ? (
          <>
            <div className="flex flex-col gap-3 rounded-v2 border border-v2-line bg-v2-sunken/60 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-v2-13 font-medium text-v2-fg">
                  Populate from onboarding intake
                </h3>
                <p className="mt-0.5 text-v2-12 text-v2-fg-3">
                  Fill user fields from an onboarding intake owner prefill.
                </p>
              </div>
              <Button onClick={onPopulateFromIntake}>Populate User</Button>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="First Name">
                {(id, describedBy) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    type="text"
                    name="first_name"
                    value={userForm.first_name}
                    onChange={onUserChange}
                    required
                  />
                )}
              </Field>
              <Field label="Last Name">
                {(id, describedBy) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    type="text"
                    name="last_name"
                    value={userForm.last_name}
                    onChange={onUserChange}
                    required
                  />
                )}
              </Field>
            </div>
            <Field label="Email">
              {(id, describedBy) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  type="email"
                  name="email"
                  value={userForm.email}
                  onChange={onUserChange}
                  required
                />
              )}
            </Field>
            <Field label="Password" hint="Must be at least 8 characters long">
              {(id, describedBy) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  type="password"
                  name="password"
                  value={userForm.password}
                  onChange={onUserChange}
                  required
                />
              )}
            </Field>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Store">
                {(id, describedBy) => (
                  <Picker
                    id={id}
                    describedBy={describedBy}
                    options={storeOptions}
                    value={userForm.store_id || ''}
                    onChange={(val) =>
                      onUserChange({
                        target: { name: 'store_id', value: val, type: 'text' },
                      } as ControlChange)
                    }
                    placeholder="Select a store..."
                    required
                    disabled={loadingStores}
                  />
                )}
              </Field>
              <Field label="Role">
                {(id, describedBy) => (
                  <Select
                    id={id}
                    aria-describedby={describedBy}
                    name="role"
                    value={userForm.role}
                    onChange={onUserChange}
                    required
                  >
                    {ROLE_OPTIONS}
                  </Select>
                )}
              </Field>
            </div>
          </>
        ) : (
          <>
            <ChoiceField
              label="Store Group User Mode"
              items={[
                { key: 'new', label: 'Create new user' },
                { key: 'existing', label: 'Attach existing user' },
              ]}
              value={groupMode}
              onChange={onGroupModeChange}
            />
            {groupMode === 'new' ? (
              <>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="First Name">
                    {(id, describedBy) => (
                      <Input
                        id={id}
                        aria-describedby={describedBy}
                        type="text"
                        value={groupForm.first_name}
                        onChange={(e) =>
                          setGroupField('first_name')(e.target.value)
                        }
                        required
                      />
                    )}
                  </Field>
                  <Field label="Last Name">
                    {(id, describedBy) => (
                      <Input
                        id={id}
                        aria-describedby={describedBy}
                        type="text"
                        value={groupForm.last_name}
                        onChange={(e) =>
                          setGroupField('last_name')(e.target.value)
                        }
                        required
                      />
                    )}
                  </Field>
                </div>
                <Field label="Email">
                  {(id, describedBy) => (
                    <Input
                      id={id}
                      aria-describedby={describedBy}
                      type="email"
                      value={groupForm.email}
                      onChange={(e) => setGroupField('email')(e.target.value)}
                      required
                    />
                  )}
                </Field>
                <Field
                  label="Password"
                  hint="Must be at least 8 characters long"
                >
                  {(id, describedBy) => (
                    <Input
                      id={id}
                      aria-describedby={describedBy}
                      type="password"
                      value={groupForm.password}
                      onChange={(e) =>
                        setGroupField('password')(e.target.value)
                      }
                      required
                    />
                  )}
                </Field>
              </>
            ) : (
              <Field label="Existing User">
                {(id, describedBy) => (
                  <Picker
                    id={id}
                    describedBy={describedBy}
                    options={storeUserOptions}
                    value={groupForm.user || ''}
                    onChange={(val) =>
                      setGroupField('user')(parseSelectId(val))
                    }
                    placeholder="Select a user..."
                    required
                    disabled={loadingStoreUsers}
                  />
                )}
              </Field>
            )}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Store Group">
                {(id, describedBy) => (
                  <Picker
                    id={id}
                    describedBy={describedBy}
                    options={storeGroupOptions}
                    value={groupForm.store_group || ''}
                    onChange={(val) =>
                      setGroupField('store_group')(parseSelectId(val))
                    }
                    placeholder="Select a store group..."
                    required
                    disabled={loadingStoreGroups}
                  />
                )}
              </Field>
              <Field label="Role">
                {(id, describedBy) => (
                  <Select
                    id={id}
                    aria-describedby={describedBy}
                    value={groupForm.role}
                    onChange={(e) =>
                      setGroupField('role')(
                        e.target.value as StoreGroupUserFormData['role'],
                      )
                    }
                    required
                  >
                    {GROUP_ROLE_OPTIONS}
                  </Select>
                )}
              </Field>
            </div>
          </>
        )}
        <Messages error={error} success={success} />
      </form>
    </Dialog>
  );
};

export const EditUserDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  form: Record<string, any> | null;
  error: string;
  success: string;
  onChange: (e: ControlChange) => void;
  storeOptions: SelectOption[];
}> = ({
  open,
  onClose,
  onSubmit,
  form,
  error,
  success,
  onChange,
  storeOptions,
}) => {
  const formId = useId();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Edit User"
      footer={
        form ? (
          <FormFooter
            formId={formId}
            submitLabel="Save Changes"
            onCancel={onClose}
          />
        ) : undefined
      }
    >
      {form ? (
        <form id={formId} onSubmit={onSubmit} className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="First Name">
              {(id, describedBy) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  type="text"
                  name="first_name"
                  value={form.first_name}
                  onChange={onChange}
                />
              )}
            </Field>
            <Field label="Last Name">
              {(id, describedBy) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  type="text"
                  name="last_name"
                  value={form.last_name}
                  onChange={onChange}
                />
              )}
            </Field>
            <Field label="Email">
              {(id, describedBy) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                />
              )}
            </Field>
            <Field label="Password" optional>
              {(id, describedBy) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  type="password"
                  name="password"
                  value={form.password}
                  onChange={onChange}
                />
              )}
            </Field>
            <Field label="Store">
              {(id, describedBy) => (
                <Picker
                  id={id}
                  describedBy={describedBy}
                  options={storeOptions}
                  value={form.store_id || ''}
                  onChange={(val) =>
                    onChange({
                      target: { name: 'store_id', value: val, type: 'text' },
                    } as ControlChange)
                  }
                  placeholder="Select a store..."
                />
              )}
            </Field>
            <Field label="Role">
              {(id, describedBy) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  name="role"
                  value={form.role}
                  onChange={onChange}
                >
                  {ROLE_OPTIONS}
                </Select>
              )}
            </Field>
          </div>
          <Messages error={error} success={success} />
        </form>
      ) : (
        <p
          role="status"
          className="flex items-center gap-2 text-v2-13 text-v2-fg-3"
        >
          <Spinner />
          Loading...
        </p>
      )}
    </Dialog>
  );
};

export const EditStoreGroupUserDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  form: StoreGroupUserFormData;
  onChange: React.Dispatch<React.SetStateAction<StoreGroupUserFormData>>;
  error: string;
  success: string;
  storeUserOptions: SelectOption[];
  loadingStoreUsers: boolean;
  storeGroupOptions: SelectOption[];
  loadingStoreGroups: boolean;
}> = ({
  open,
  onClose,
  onSubmit,
  form,
  onChange,
  error,
  success,
  storeUserOptions,
  loadingStoreUsers,
  storeGroupOptions,
  loadingStoreGroups,
}) => {
  const formId = useId();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Edit Store Group User"
      footer={
        <FormFooter
          formId={formId}
          submitLabel="Save Changes"
          onCancel={onClose}
        />
      }
    >
      <form id={formId} onSubmit={onSubmit} className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="User">
            {(id, describedBy) => (
              <Picker
                id={id}
                describedBy={describedBy}
                options={storeUserOptions}
                value={form.user || ''}
                onChange={(val) =>
                  onChange((prev) => ({
                    ...prev,
                    user: parseSelectId(val),
                  }))
                }
                placeholder="Select a user..."
                required
                disabled={loadingStoreUsers}
              />
            )}
          </Field>
          <Field label="Store Group">
            {(id, describedBy) => (
              <Picker
                id={id}
                describedBy={describedBy}
                options={storeGroupOptions}
                value={form.store_group || ''}
                onChange={(val) =>
                  onChange((prev) => ({
                    ...prev,
                    store_group: parseSelectId(val),
                  }))
                }
                placeholder="Select a store group..."
                required
                disabled={loadingStoreGroups}
              />
            )}
          </Field>
          <Field label="Role">
            {(id, describedBy) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={form.role}
                onChange={(e) =>
                  onChange((prev) => ({
                    ...prev,
                    role: e.target.value as StoreGroupUserFormData['role'],
                  }))
                }
                required
              >
                {GROUP_ROLE_OPTIONS}
              </Select>
            )}
          </Field>
        </div>
        <Messages error={error} success={success} />
      </form>
    </Dialog>
  );
};

export interface IntakePickerControls {
  open: boolean;
  intakes: OnboardingIntake[];
  selectedId: string;
  onSelect: (id: string) => void;
  overwrite: boolean;
  onOverwriteChange: (overwrite: boolean) => void;
  loading: boolean;
  error: string;
  onApply: () => void;
  onClose: () => void;
}

export const IntakePrefillDialog: React.FC<{
  picker: IntakePickerControls;
}> = ({ picker }) => (
  <Dialog
    open={picker.open}
    onClose={picker.onClose}
    title="Populate from Onboarding Intake"
    footer={
      <>
        <Button variant="ghost" onClick={picker.onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={picker.onApply}
          disabled={picker.loading || !picker.selectedId}
        >
          {picker.loading ? 'Applying...' : 'Apply Prefill'}
        </Button>
      </>
    }
  >
    <div className="flex flex-col gap-4">
      <Field label="Onboarding Intake">
        {(id, describedBy) => (
          <Picker
            id={id}
            describedBy={describedBy}
            options={picker.intakes.map((intake) => ({
              value: intake.id,
              label: intakeOptionLabel(intake),
            }))}
            value={picker.selectedId}
            onChange={picker.onSelect}
            placeholder={
              picker.loading
                ? 'Loading intakes...'
                : 'Select an onboarding intake'
            }
            required
            disabled={picker.loading}
          />
        )}
      </Field>
      <Checkbox
        label="Overwrite existing fields instead of filling only blank fields"
        checked={picker.overwrite}
        onChange={(e) => picker.onOverwriteChange(e.target.checked)}
      />
      {picker.error && <Callout tone="danger">{picker.error}</Callout>}
    </div>
  </Dialog>
);
